import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { useInfiniteScrollSentinel } from "@/my_components/Common/useInfiniteScrollSentinel";
import EventCard from "@/my_components/Common/EventCard";
import HostEventPromo from "@/my_components/_EventDetails/sections/HostEventPromo";
import { getEvents } from "@/services/eventService";
import { getErrorMessage } from "@/services/apiClient";
import { ROUTE } from "@/route/app_routes.js";

const Events = () => {
  const { t } = useTranslation("events");
  const router = useRouter();
  const search = typeof router.query.search === "string" ? router.query.search : "";
  const upcoming = typeof router.query.upcoming === "string" ? router.query.upcoming : "";

  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // The cascade is only used for the DEFAULT listing (no active search term) -- search remains
  // an escape hatch, unaffected by location ranking. Page-1's event IDs are remembered so
  // page 2+ can ask the backend to exclude them once it drops the cascade (see
  // EventController::index's `nearby`/`exclude` params) -- otherwise an event could repeat
  // once the city/state/country tiers are exhausted and the plain listing takes over.
  const useCascade = !search;
  const firstPageIdsRef = useRef([]);

  useEffect(() => {
    setPage(1);
  }, [search, upcoming]);

  useEffect(() => {
    const isFirstPage = page === 1;
    if (isFirstPage) setLoading(true);
    else setLoadingMore(true);

    getEvents({
      page,
      search,
      upcoming: upcoming === "false" ? false : true,
      nearby: useCascade,
      exclude: !isFirstPage ? firstPageIdsRef.current : [],
    })
      .then((res) => {
        const data = res.data || [];
        if (isFirstPage) firstPageIdsRef.current = data.map((e) => e.id);
        setEvents((prev) => (isFirstPage ? data : [...prev, ...data]));
        setTotalPages(res.meta?.last_page || 0);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }, [page, search, upcoming]); // eslint-disable-line react-hooks/exhaustive-deps

  const canLoadMore = !loading && !loadingMore && page < totalPages;
  const loadMore = useCallback(() => setPage((p) => p + 1), []);
  const sentinelRef = useInfiniteScrollSentinel(loadMore, canLoadMore);

  if (loading) {
    return (
      <div className="rbt-event-grid">
        {Array.from({ length: 6 }).map((_, i) => (
          <div className="rbt-event-card-light" key={i}>
            <div className="rbt-skel" style={{ aspectRatio: "16/10" }} />
            <div className="rbt-event-card-light__body">
              <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w60" style={{ height: 10 }} />
              <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w90" style={{ height: 16, marginTop: 6 }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    // This is the one event surface on the platform that shows a message instead of hiding
    // when there's nothing to show -- it's the dedicated events destination, so "nothing here"
    // needs to be said, not silently disappear. Search keeps its own generic copy (an active
    // search term is the user's own override, unrelated to location); the no-search case is
    // specifically about the visitor's own area having nothing listed right now.
    const copy = useCascade
      ? {
          title: t("listing.empty.nearbyTitle", "No events in your area yet"),
          description: t(
            "listing.empty.nearbyDescription",
            "We don't have any events near you right now. Check back soon, or be the first to create one."
          ),
        }
      : {
          title: t("listing.empty.title", "No events match that yet"),
          description: t("listing.empty.description", "Try a different search, check past events, or be the first to create one."),
        };

    return (
      <div className="rbt-course-empty">
        <i className="feather-calendar"></i>
        <h3>{copy.title}</h3>
        <p>{copy.description}</p>
        <Link className="rbt-btn btn-gradient radius-round" href={ROUTE.dashboardEventsCreate}>
          {t("listing.empty.createButton", "Create an Event")}
        </Link>
      </div>
    );
  }

  // The "host your own event" promo drops into the grid itself as its own full-width row --
  // after the 6th card (end of row 2 on the 3-column desktop layout) so a visitor sees a few
  // real events first, or at the end of a shorter list -- rather than as a separate section
  // bookending the whole grid.
  const promoIndex = Math.min(6, events.length);

  return (
    <>
      <div className="rbt-event-grid">
        {events.slice(0, promoIndex).map((event) => (
          <EventCard key={event.id} event={event} />
        ))}

        <div className="rbt-event-grid__promo">
          <HostEventPromo compact bare />
        </div>

        {events.slice(promoIndex).map((event) => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>

      {loadingMore && (
        <div className="rbt-event-grid mt--30">
          {Array.from({ length: 3 }).map((_, i) => (
            <div className="rbt-event-card-light" key={`more-${i}`}>
              <div className="rbt-skel" style={{ aspectRatio: "16/10" }} />
              <div className="rbt-event-card-light__body">
                <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w60" style={{ height: 10 }} />
                <div className="rbt-skel rbt-course-card__skel-line rbt-course-card__skel-line--w90" style={{ height: 16, marginTop: 6 }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sentinel: an empty div the IntersectionObserver watches -- scrolling it into view
          (with a 400px lookahead margin) triggers the next page's fetch. */}
      {page < totalPages && <div ref={sentinelRef} aria-hidden="true" />}

      {page >= totalPages && (
        <p className="rbt-infinite-end">
          <i className="feather-check-circle"></i> {t("listing.infiniteEnd", "You've reached the end — that's every event matching your filters.")}
        </p>
      )}
    </>
  );
};

export default Events;
